import { IAbstractMetadata } from 'library/ts/interfaces/metadata/abstract/IAbstractMetadata';
import { IAPIDownloadCountry } from 'library/ts/interfaces/metadata/api/IAPIDownloadCountry';
import { IAPIPlaybackEvents } from 'library/ts/interfaces/metadata/api/IAPIPlaybackEvents';
import { IAPIRegionalRating } from 'library/ts/interfaces/metadata/api/IAPIRegionalRating';
import { IAPIRestriction } from 'library/ts/interfaces/metadata/api/IAPIRestriction';
import { IAPIThumbnail } from 'library/ts/interfaces/metadata/api/IAPIThumbnail';
import { IContentCANVideoMetadata } from 'library/ts/interfaces/metadata/api/IContentCANVideoMetadata';
import { MPXAddOn } from 'library/ts/model/enums/mpx/MPXAddOn';
import { MPXAssetType } from 'library/ts/model/enums/mpx/MPXAssetType';
import { MPXAvailableProfileType } from 'library/ts/model/enums/mpx/MPXAvailableProfileType';
import { MPXBrand } from 'library/ts/model/enums/mpx/MPXBrand';
import { MPXDeviceType } from 'library/ts/model/enums/mpx/MPXDeviceType';
import { MPXGenre } from 'library/ts/model/enums/mpx/MPXGenre';
import { MPXMediaType } from 'library/ts/model/enums/mpx/MPXMediaType';
import { MPXPremiumFeatures } from 'library/ts/model/enums/mpx/MPXPremiumFeatures';
import { MPXStatus } from 'library/ts/model/enums/mpx/MPXStatus';
import { MPXSubscriptionLevel } from 'library/ts/model/enums/mpx/MPXSubscriptionLevel';
import { MPXVideoProperty } from 'library/ts/model/enums/mpx/MPXVideoProperty';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Describes API video metadata item object
 * @see {@link https://paramount.atlassian.net/browse/CBSVIDEO-5299|CBSVIDEO-5299}
 * @see {@link https://paramount.atlassian.net/wiki/spaces/PTC/pages/16123513/CAN+Data+Dictionary+-+Video+Object|API Video Object}
 * @see {@link https://paramount.atlassian.net/wiki/spaces/PLVIDOPS/pages/47416613/Live+TV+Web+API+Spec#CANResponseModel|CANResponseModel}
 */
export interface IAPIMetadataItem extends IAbstractMetadata {
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	__FOR_TRACKING_ONLY_MEDIA_ID?: number;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date the video first aired on television. Human-readable format. String (Date)
	_airDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date the video first aired on television. ISO Format. String (Date)
	_airDateISO?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The Last Modified Date of a video release in MPX in Human-readable Format. String (Date)
	_editDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date a video (release added date) was first ingested into CAN in Human-readable Format. String (Date)
	_firstIngestDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The last modified date of a video in Human-readable Format. String (Date)
	_ingestDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The Last Modified Date of a video release in CAN in Human-readable Format. String (Date)
	_lastModifiedDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date the video release was added into CAN. Human-readable format. String (Data)
	_pubDate?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	_pubDateISO?: string;
	// AddOn packages video is associated with
	addOns?: MPXAddOn[];
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date the video release was added into CAN. ISO Format. String (Data)
	airDate?: number;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Category Level Amazon URL used to link back amazon's EST site
	amazonESTURL?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Unique Show Key string used for Apple Watch integration
	appleWatchListShowKey?: string;
	// The asset type of the video release
	assetType?: MPXAssetType;
	availableForProfileTypes?: MPXAvailableProfileType[];
	// A value that denotes the original source owner. (Category Object level in MPX)
	brand?: MPXBrand;
	// A space separated list of a video's Secondary Category Ids
	category?: string;
	// Unique Show ID generated by CBS
	cbsShowId?: number;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Video ID field in MPX used to link a parent video to a child video (ex: Movie to Trailer)
	childContentId?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Default Closed Caption file for the video
	closedCaptionUrl?: string;
	// Account ID of the CMS this video was published in
	cmsAccountId?: string;
	// The MPX Reference ID (Media GUID)
	contentId?: string;
	// Nested CAN video metadata (some responses provide metadata here instead of top-level)
	contentCANVideo?: IContentCANVideoMetadata;
	// Copyright string — optional; no equivalent in Pluto TV / Stitcher metadata
	copyright?: string;
	// Single value string that denotes the DAI Stream Key value for a specific Live Video Asset
	daistreamKey?: string;
	// The full description of a video
	description?: string;
	// A flag to denote if a video release was published (encoded) for use on a PC or Mobile device
	deviceType?: MPXDeviceType;
	// Set of downloadable flags per country
	downloadCountrySet?: IAPIDownloadCountry[];
	// The length of a video in seconds
	duration?: number;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The Last Modified Date of a video release in MPX in Epoch Time
	editDate?: number;
	// Embeddable flag — optional; no equivalent in Pluto TV / Stitcher metadata
	embeddable?: string;
	// Time in minutes:seconds that tells the time the end credits will roll
	endCreditsChapterTime?: string;
	// The episode number of the video (does not reset at the end of the season)
	episodeNum?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Boolean that tells if this video should be excluded from nielsen tracking
	excludeNielsenTracking?: boolean;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// A boolean value that denotes if a video is flagged as internal (exclusive) or not
	exclusive?: boolean;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// A flag that denotes whether a video release is expired
	expired?: boolean;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date a video was first ingested into CAN (release added date). Epoch Time
	firstIngestDate?: number;
	// A boolean value that denotes if a video is a full episode or a clip (long form/short form)
	fullEpisode?: boolean;
	// The genre of a video. Custom set of valued curated in MPX
	genre?: MPXGenre;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date a video (release added date) was first ingested into CAN in Epoch Time
	ingestDate?: number;
	// Boolean flag that tells if a video is a Live Streaming video or VOD
	isLive?: boolean;
	// Boolean flag that tells if the content is intended for kids
	isKidsContent?: boolean;
	// Boolean flag that tells if a video is DRM protected
	isProtected?: boolean;
	// Boolean flag that tells if the video container a product placement warning
	isProductPlacement?: boolean;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	itunesESTURL?: string;
	// Short form of a video's Title
	label?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The Last Modified Date of a video release in CAN in Epoch Time
	lastModifiedDate?: number;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Video liveStreamingUrl
	liveStreamingUrl?: string;
	// The date a video becomes available (playable). ISO Formats
	mediaAvailableDate?: string;
	// The date the video becomes available (playable). Epoch Time
	mediaAvailableDateEpoch?: number;
	// Single value attribute that tells if a video is a Full Episode, Clip, Promo Full Episode, etc
	mediaType?: MPXMediaType;
	// A unique video release ID generated by MPX
	pid?: string;
	// Multi value list of Playback Event values (ex: skip preview, skip recap)
	playbackEvents?: IAPIPlaybackEvents;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// URL to the CAN player for the video
	playerLocUrl?: string;
	// Boolean flag that denotes if a video was encoded with premium audio
	premiumAudioAvailable?: boolean;
	// Multi value list of premium features the video was encoded with
	premiumFeatures?: MPXPremiumFeatures[];
	// The MPX Primary Category ID associated to the video
	primaryCategory?: string;
	// The MPX Primary Category's Full Title that is associated the video
	primaryCategoryName?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// The date a video was first ingested into CAN (release added date). Epoch Time
	pubDate?: number;
	// Single rating string containing either one or more sets of ratings
	rating?: string;
	// Structured Rating element designed to return multiple ratings per region
	regionalRatings?: IAPIRegionalRating[];
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Restrictions in stream
	restriction?: IAPIRestriction;
	// The season number of the video
	seasonNum?: string;
	// The series title of the video
	seriesTitle?: string;
	// A shortened version of a video's description
	shortDescription?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Formatted URL to a video's show page on cbs.com
	showPageUrl?: string;
	// Flag used by CBS / AA / P+ to determine if a video is allowed to be played
	status?: MPXStatus;
	// URL to the video used for playback
	streamingUrl?: string;
	// Flag used by CBS / AA / P+ to determine if a video is behind a paywall. String (List of Key/Values, String:boolean)
	subscriptionLevel?: MPXSubscriptionLevel;
	// Default thumbnail for this video in MPX
	thumbnail?: string;
	thumbnailSet?: IAPIThumbnail[];
	// Set of additional thumbnails published for this video
	thumbnailSheetSet?: IAPIThumbnail[];
	// The full title of a video
	title?: string;
	// INFO: videoTitle is a copy of title property injected by Smart Tag Model
	videoTitle?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// TMS Program (Episode) ID for the video
	tmsprogramID?: string;
	// TMS Series ID for the video
	tmsseriesID?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// A formatted version of the Content Owner (Provider field in MPX)
	topLevelCategory?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Formatted URL to a video's page on cbs.com
	url?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Formatted URL to a video's page on cbs.com
	videoPageUrl?: string;
	// Multi value list of a video's properties
	videoProperties?: MPXVideoProperty[];
	// VTAG params string, used for pause ads — optional; no equivalent in Pluto TV / Stitcher metadata
	vTag?: string;
	/** @deprecated Not read by Smart Tag; kept for pass-through contract compatibility. */
	// Youtube Ids
	youtubeIds?: string[];
}

/**
 * @description Returns IAPIMetadataItem object with undefined properties
 * @returns {IAPIMetadataItem}
 */
export const apiMetadataItemBlueprint = (): IAPIMetadataItem => {
	return new class implements IAPIMetadataItem {
		__FOR_TRACKING_ONLY_MEDIA_ID = undefined;
		_airDate = undefined;
		_airDateISO = undefined;
		_editDate = undefined;
		_firstIngestDate = undefined;
		_ingestDate = undefined;
		_lastModifiedDate = undefined;
		_pubDate = undefined;
		_pubDateISO = undefined;
		addOns: undefined;
		airDate = undefined;
		amazonESTURL = undefined;
		appleWatchListShowKey = undefined;
		assetType = undefined;
		availableForProfileTypes = undefined;
		brand = undefined;
		category = undefined;
		cbsShowId = undefined;
		childContentId = undefined;
		closedCaptionUrl = undefined;
		cmsAccountId = undefined;
		contentId = undefined;
		copyright = undefined;
		daistreamKey = undefined;
		description = undefined;
		deviceType = undefined;
		downloadCountrySet = undefined;
		duration = undefined;
		editDate = undefined;
		embeddable = undefined;
		endCreditsChapterTime = undefined;
		episodeNum = undefined;
		excludeNielsenTracking: boolean;
		exclusive: boolean;
		expired: boolean;
		firstIngestDate = undefined;
		fullEpisode: boolean;
		genre = undefined;
		ingestDate = undefined;
		isLive: boolean;
		isKidsContent: boolean;
		isProtected: boolean;
		isProductPlacement: boolean;
		itunesESTURL = undefined;
		label = undefined;
		lastModifiedDate = undefined;
		mediaAvailableDate = undefined;
		mediaAvailableDateEpoch = undefined;
		mediaType = undefined;
		pid = undefined;
		playbackEvents = undefined;
		playerLocUrl = undefined;
		premiumAudioAvailable = undefined;
		premiumFeatures = undefined;
		primaryCategory = undefined;
		primaryCategoryName = undefined;
		pubDate = undefined;
		rating = undefined;
		regionalRatings = undefined;
		restriction = undefined;
		seasonNum = undefined;
		seriesTitle = undefined;
		shortDescription = undefined;
		showPageUrl = undefined;
		status = undefined;
		streamingUrl = undefined;
		subscriptionLevel = undefined;
		thumbnail = undefined;
		thumbnailSet = undefined;
		thumbnailSheetSet = undefined;
		title = undefined;
		tmsprogramID = undefined;
		tmsseriesID = undefined;
		topLevelCategory = undefined;
		url = undefined;
		videoPageUrl = undefined;
		videoProperties = undefined;
		videoTitle = undefined;
		vTag = undefined;
		youtubeIds = undefined;
	};
};